import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  DeleteButton,
  OriginBadge,
} from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import {
  PennField,
  PennInput,
  PennCheckbox,
  PennDropdown,
} from "@/components/ui";
import {
  useUsers,
  useUpdateUser,
  useRoles,
  useAssignRole,
  useDeleteUser,
} from "@/hooks/useUsers";
import type { AdminUser } from "@/api/users";
import type { RoleCode, PennRole } from "@/types/auth-types/AuthUser";

const fullName = (u: AdminUser) =>
  [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || "—";

// "User type" filter: origin of the account. Internal = registered through the
// admin (internal) app / AWS Cognito internal group; External = registered
// through the external app (external customers + distributor partners).
type UserTypeFilter = "all" | "internal" | "external";
const USER_TYPE_OPTIONS: { value: UserTypeFilter; label: string }[] = [
  { value: "all", label: "All users" },
  { value: "internal", label: "Internal users" },
  { value: "external", label: "External users" },
];

// An external-origin account may only hold EXTERNAL roles (role_master.
// is_internal = false — the two §D.1 external seeds plus any external custom
// role); it can never be promoted to an internal (staff) role. Mirrors the
// svc-03 assignRole gate (external→internal is rejected with 422).
// Internal users get internal roles PLUS the two canonical external seeds
// (so an admin can demote a staff account to customer/distributor) — but not
// external CUSTOM roles.
const CANONICAL_EXTERNAL_ROLE_CODES: ReadonlySet<string> = new Set([
  "external_customer",
  "distributor_partner",
]);

// Profile fields accepted by PATCH /v1/users/:id. (Role is saved separately
// via the role endpoint; email is not updatable.)
const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(30, "First name must be 30 characters or fewer"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(30, "Last name must be 30 characters or fewer"),
  // Phone is optional; capped at 30 to match the users.phone VARCHAR(30) column.
  phone: z
    .string()
    .max(30, "Phone number must be 30 characters or fewer")
    .optional()
    .or(z.literal("")),
  companyName: z
    .string()
    .max(100, "Max 100 characters")
    .optional()
    .or(z.literal("")),
  companyAddress: z
    .string()
    .max(500, "Max 500 characters")
    .optional()
    .or(z.literal("")),
});
type ProfileFormData = z.infer<typeof profileSchema>;

// All 9 permission booleans (rule §D.1), rendered read-only from the role.
const PERMISSION_LABELS: { key: keyof PennRole; label: string }[] = [
  { key: "canCreateApplications", label: "Can create applications" },
  { key: "canWorkForOthers", label: "Can work for others" },
  { key: "canSeePrices", label: "Can see prices" },
  { key: "canGenerateQuote", label: "Can generate quote" },
  { key: "canGenerateRoi", label: "Can generate ROI" },
  { key: "canApproveEngineering", label: "Can approve engineering" },
  { key: "canSwitchRegion", label: "Can switch region" },
  { key: "canManageUsers", label: "Can manage users" },
  { key: "canManageMasterData", label: "Can manage master data" },
];

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (
      err as { response?: { data?: { error?: { message?: string } } } }
    ).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

// Mirrors userPage StatusBadge so Flags share the same green status UI.

// const StatusBadge = ({ status }: { status: boolean }) => (
//   <span
//     className={`inline-flex items-center gap-2 px-3 py-0.5 rounded-full text-small-medium border ${
//       status
//         ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-800"
//         : "bg-gray-100 text-blue_grey3 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
//     }`}
//   >
//     <span
//       className={`w-1.5 h-1.5 rounded-full ${status ? "bg-green-500" : "bg-gray-400"}`}
//     />
//     {status ? "Active" : "Inactive"}
//   </span>
// );

type ModalMode = "view" | "edit";
type ModalState = { mode: ModalMode; user: AdminUser } | null;

export const UsersPage = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState<UserTypeFilter>("all");
  // Role filter — URL-driven (`?role=<code>`) so the Roles page "Users
  // Assigned" count can deep-link here pre-filtered.
  const [searchParams, setSearchParams] = useSearchParams();
  const roleFilter = searchParams.get("role") ?? "";
  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedRoleCode, setSelectedRoleCode] = useState<RoleCode | "">("");

  const updateMutation = useUpdateUser();
  const assignRoleMutation = useAssignRole();
  const deleteMutation = useDeleteUser();
  const { data: roles = [] } = useRoles();
  const saving = updateMutation.isPending || assignRoleMutation.isPending;

  // Debounce the search box and reset to the first page on a new query.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPageIndex(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      skip: pageIndex * pageSize,
      take: pageSize,
      ...(search ? { search } : {}),
      // undefined ("All") is omitted by axios; internal → true, external → false.
      ...(userType === "all" ? {} : { isInternal: userType === "internal" }),
      ...(roleFilter ? { roleCode: roleFilter } : {}),
    }),
    [pageIndex, pageSize, search, userType, roleFilter]
  );

  const { data, isLoading, isFetching, isError, error } = useUsers(params);
  const rows = data ?? [];
  // No total count from the API → a full page implies there may be a next one.
  const hasNext = rows.length === pageSize;

  const isView = modal?.mode === "view";
  const editingUser = modal?.user;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  const open = (mode: ModalMode, user: AdminUser) => {
    setFormError(null);
    reset({
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      phone: user.phone ?? "",
      companyName: user.companyName ?? "",
      companyAddress: user.companyAddress ?? "",
    });
    setSelectedRoleCode(user.role?.code ?? "");
    setModal({ mode, user });
  };

  const onSubmit = async (formData: ProfileFormData) => {
    if (!editingUser) return;
    setFormError(null);
    try {
      await updateMutation.mutateAsync({
        id: editingUser.id,
        body: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: formData.phone?.trim() || null,
          companyName: formData.companyName?.trim() || null,
          companyAddress: formData.companyAddress?.trim() || null,
        },
      });
      // Persist a role change when one was made. The role must be offerable
      // for the user's type (mirrors the dropdown filter: internal users may
      // take internal roles or a canonical external seed; external users only
      // external roles); svc-03 re-rejects a mismatch, but we re-check here to
      // guard against tampered state.
      const roleChanged =
        !!selectedRoleCode && selectedRoleCode !== editingUser.role?.code;
      const selectedRoleRow = roles.find((r) => r.code === selectedRoleCode);
      const roleAllowedForOrigin = editingUser.isInternal
        ? selectedRoleRow?.isInternal === true ||
          CANONICAL_EXTERNAL_ROLE_CODES.has(selectedRoleCode)
        : selectedRoleRow?.isInternal === false;
      if (roleChanged && roleAllowedForOrigin) {
        await assignRoleMutation.mutateAsync({
          id: editingUser.id,
          roleCode: selectedRoleCode,
        });
      }
      setModal(null);
    } catch (err) {
      setFormError(apiErrorMessage(err, "Failed to update user."));
    }
  };

  // Permissions follow the selected role (§D.1) — read-only, preview live.
  const selectedRole = roles.find((r) => r.code === selectedRoleCode);
  // Every ACTIVE role is assignable — the 5 §D.1 seeds and admin-created
  // custom roles alike (ADR-0021; svc-03 rejects a deactivated role with 422).
  // Internal users: internal (staff) roles + the two canonical external seeds
  // (demotion path). External users: external roles only.
  const roleOptions = roles
    .filter((r) => r.isActive)
    .filter((r) =>
      !editingUser
        ? true
        : editingUser.isInternal
          ? r.isInternal || CANONICAL_EXTERNAL_ROLE_CODES.has(r.code)
          : !r.isInternal,
    )
    .map((r) => ({ value: r.code, label: r.displayName }));

  const cols: ColDef<AdminUser>[] = [
    { header: "Email", key: "email" },
    { header: "Name", render: fullName },
    { header: "Company", render: (u) => u.companyName ?? "—" },
    {
      header: "Role",
      // An internal account on a customer-level role has no staff access yet
      // and is treated as a customer everywhere; say so instead of showing
      // the customer role's name beside Type "Internal".
      render: (u) =>
        u.isInternal && u.role && !u.role.isInternal ? (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            No role assigned
          </span>
        ) : (
          (u.role?.displayName ?? "—")
        ),
    },
    { header: "Region", render: (u) => u.region?.displayName ?? "—" },
    { header: "Department", render: (u) => u.department?.displayName ?? "—" },
    { header: "Type", render: (u) => <OriginBadge isInternal={u.isInternal} /> },
    // {
    //   header: "Flags",
    //   render: (u) => (
    //     <div className="flex gap-1">
    //       <StatusBadge status={u.isActive} />
    //     </div>
    //   ),
    // },
    {
      header: "Actions",
      render: (u) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => open("view", u)} />
          <EditButton onClick={() => open("edit", u)} />
          <DeleteButton onClick={() => setDeleteTarget(u)} />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="All Users"
        subtitle="Edit/delete users. Only admins can change price visibility and admin rights."
      />

      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="w-full max-w-sm">
          <PennInput
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email…"
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="w-48">
            <PennDropdown
              inputId="roleFilter"
              name="roleFilter"
              options={[
                { value: "", label: "All roles" },
                ...roles.map((r) => ({ value: r.code, label: r.displayName })),
              ]}
              value={roleFilter}
              onChange={(val) => {
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    if (val) next.set("role", String(val));
                    else next.delete("role");
                    return next;
                  },
                  { replace: true },
                );
                setPageIndex(0);
              }}
              placeholder="Role…"
            />
          </div>
          <div className="w-48">
            <PennDropdown
              inputId="userType"
              name="userType"
              options={USER_TYPE_OPTIONS}
              value={userType}
              onChange={(val) => {
                setUserType((val as UserTypeFilter) ?? "all");
                setPageIndex(0);
              }}
              placeholder="User type…"
            />
          </div>
        </div>
      </div>

      {isError && (
        <div className="mb-4 rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-body text-red-700 dark:text-red-400">
          Failed to load users
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <AdminTable
        columns={cols}
        rows={rows}
        keyExtractor={(u) => u.id}
        loading={isLoading}
        emptyMessage={
          search ? `No users match “${search}”.` : "No users found."
        }
      />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />

      <AdminModal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={isView ? "View user" : "Edit user"}
        footer={
          isView ? (
            <CancelButton onClick={() => setModal(null)} />
          ) : (
            <>
              <CancelButton onClick={() => setModal(null)} />
              <SaveButton
                onClick={handleSubmit(onSubmit)}
                label={saving ? "Saving…" : "Save changes"}
              />
            </>
          )
        }
      >
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {formError && (
            <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-body text-red-700 dark:text-red-400">
              {formError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PennField
              label="First name"
              htmlFor="firstName"
              required={!isView}
              error={!isView ? errors.firstName?.message : undefined}
            >
              <PennInput
                id="firstName"
                disabled={isView}
                error={!isView && !!errors.firstName}
                placeholder="First name"
                maxLength={30}
                {...register("firstName")}
              />
            </PennField>
            <PennField
              label="Last name"
              htmlFor="lastName"
              required={!isView}
              error={!isView ? errors.lastName?.message : undefined}
            >
              <PennInput
                id="lastName"
                disabled={isView}
                error={!isView && !!errors.lastName}
                placeholder="Last name"
                maxLength={30}
                {...register("lastName")}
              />
            </PennField>
          </div>
          <PennField label="Email (ID)" htmlFor="email">
            <PennInput
              id="email"
              type="email"
              disabled
              value={editingUser?.email ?? ""}
              readOnly
            />
          </PennField>
          <PennField
            label="Phone number"
            htmlFor="phone"
            error={!isView ? errors.phone?.message : undefined}
          >
            <PennInput
              id="phone"
              type="tel"
              disabled={isView}
              error={!isView && !!errors.phone}
              placeholder="(555) 123-4567"
              maxLength={30}
              {...register("phone")}
            />
          </PennField>
          <PennField
            label="Company name"
            htmlFor="companyName"
            error={!isView ? errors.companyName?.message : undefined}
          >
            <PennInput
              id="companyName"
              disabled={isView}
              error={!isView && !!errors.companyName}
              placeholder="Company name"
              {...register("companyName")}
            />
          </PennField>
          <PennField
            label="Company address"
            htmlFor="companyAddress"
            error={!isView ? errors.companyAddress?.message : undefined}
          >
            <PennInput
              id="companyAddress"
              disabled={isView}
              error={!isView && !!errors.companyAddress}
              placeholder="Street, ZIP, City, Country"
              {...register("companyAddress")}
            />
          </PennField>
          <PennField label="Role" htmlFor="role">
            <PennDropdown
              inputId="role"
              name="role"
              options={roleOptions}
              value={selectedRoleCode}
              onChange={(val) => setSelectedRoleCode((val as RoleCode) ?? "")}
              placeholder="Select a role…"
              isDisabled={isView}
              isSearchable
            />
            {!isView && !editingUser?.isInternal && (
              <p className="mt-2 text-small text-amber-600 dark:text-amber-400">
                This is an externally-registered user — you can only assign an
                external (non-staff) role. Internal (staff) roles are not
                available for external users.
              </p>
            )}
          </PennField>
          <div className="pt-1">
            <p className="text-small text-blue_grey4 mb-2">
              Permissions are determined by the role and update when you change
              it.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              {PERMISSION_LABELS.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-2 text-body text-blue_grey cursor-not-allowed opacity-80"
                >
                  <PennCheckbox
                    disabled
                    readOnly
                    checked={!!selectedRole?.[key]}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </form>
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        itemLabel={deleteTarget ? fullName(deleteTarget) : ""}
      />
    </>
  );
};
